import {MithrilComponent} from '@bitstillery/mithril'
import {classes, present} from '@bitstillery/common/lib/utils'
import {Icon} from '@bitstillery/common/components/ui/icon/icon'
import {modelref_adapter, modelref_assign} from '@bitstillery/common/lib/store'
import i18next from 'i18next'
import m from '@bitstillery/mithril'

import type {FieldSelectAttrs, SelectOptionValue} from '@bitstillery/common/types/field'
import {failure_message} from '@bitstillery/common/lib/validation'

function artkey_of(value: unknown): unknown {
    return typeof value === 'object' && value !== null && 'artkey' in value ? value.artkey : undefined
}

export class FieldSelect<V extends SelectOptionValue = SelectOptionValue> extends MithrilComponent<FieldSelectAttrs<V>> {
    override oninit(vnode: m.Vnode<FieldSelectAttrs<V>>) {
        const options = vnode.attrs.options ?? []
        const {model_value, model_ref} = modelref_adapter(vnode.attrs.model)
        const empty_option_value: unknown[] = ['', null, undefined]
        const has_placeholder_option = 'placeholder' in vnode.attrs

        // The default selected value is the first option; but that is only
        // The visible value, not the actual value. With a placeholder, we
        // Assume the user will select an option, so we don't auto-select.
        // `placeholder` may be '' (blank first row); use `in` so empty string still counts.
        if (!has_placeholder_option && options.length > 0 && empty_option_value.includes(model_value) && model_ref) {
            modelref_assign(model_ref, present(options[0], 'first option').value)
        }
    }

    view(vnode: m.Vnode<FieldSelectAttrs<V>>) {
        const options = vnode.attrs.options ?? []
        const {validation} = vnode.attrs
        const invalid = validation ? validation._invalid : false
        // Add human readable description to the validation object.
        if (validation && vnode.attrs.label) {
            validation.description = vnode.attrs.label
        }

        const {model_value, model_ref} = modelref_adapter(vnode.attrs.model)

        // Augment the options with a disabled property if it is missing.
        for (const option of options) {
            if (!('disabled' in option)) {
                option.disabled = false
            }
        }

        return (
            <div
                className={classes(vnode.attrs.composed ? null : 'c-field-select field', vnode.attrs.className, {
                    disabled: vnode.attrs.disabled || vnode.attrs.loading,
                    invalid: validation && invalid && validation.dirty,
                    valid: !validation || !invalid,
                })}
            >
                {vnode.attrs.label && (
                    <label>
                        {vnode.attrs.label}
                        {vnode.attrs.icon && <Icon name={vnode.attrs.icon} />}
                        {validation && <span className='validation'>{validation.label}</span>}
                    </label>
                )}
                <div className='control'>
                    <select
                        disabled={vnode.attrs.disabled || vnode.attrs.loading}
                        onchange={(e) => {
                            if (vnode.attrs.validation) {
                                vnode.attrs.validation.dirty = true
                            }
                            let matched_value: V | ''
                            if (e.currentTarget.value !== '') {
                                matched_value = present(options[Number(e.currentTarget.value)], 'selected option').value
                            } else {
                                matched_value = ''
                            }
                            if (model_ref) {
                                modelref_assign(model_ref, matched_value)
                            }

                            // Please do not use this method to adjust state, but only
                            // For non-state side-effects where a watcher is overkill.
                            if (vnode.attrs.onchange) {
                                vnode.attrs.onchange(matched_value)
                            }
                        }}
                        tabindex={vnode.attrs.tabindex}
                    >
                        {vnode.attrs.loading && <option>Loading...</option>}
                        {'placeholder' in vnode.attrs && <option value=''>{vnode.attrs.placeholder}</option>}
                        {!vnode.attrs.loading &&
                            options.map((option, index) => {
                                const attrs: {disabled?: boolean; selected?: boolean; value: string} = {value: `${index}`}

                                const option_is_on_artkey =
                                    model_value &&
                                    artkey_of(model_value) &&
                                    artkey_of(model_value) === artkey_of(present(option.value, 'option value'))
                                const option_is_selected = model_value === option.value

                                if (!option.disabled && (option_is_selected || option_is_on_artkey)) {
                                    attrs.selected = true
                                }

                                attrs.disabled = Boolean(option.disabled)

                                let {label} = option
                                // A label that's already markup has no translation key.
                                if (vnode.attrs.translate && (typeof label === 'string' || typeof label === 'number')) {
                                    const {prefix, label: fn} = vnode.attrs.translate
                                    const key = fn ? fn(String(label)) : String(label)
                                    label = i18next.t(`${prefix}${key}`)
                                }
                                return <option {...attrs}>{label}</option>
                            })}
                    </select>
                    {vnode.children}
                </div>

                {(() => {
                    if (invalid && validation?.dirty) {
                        return <div className='help validation'>{failure_message(invalid)}</div>
                    } else if (vnode.attrs.help) {
                        return <div class='help'>{vnode.attrs.help}</div>
                    }
                    return undefined
                })()}
            </div>
        )
    }
}
